/**
 * KND-2 — how the three termination audit actions read in a Verlauf.
 *
 * The KND-1 commands write one row per stream (tenant AND unit, Regel 49) with
 * a namespaced `termination` payload. Without a branch the tenant history fell
 * through to the generic field diff and printed the nested object, and the
 * unit history said "Unit bearbeitet" for a tenant's termination. Both streams
 * read the same row shape, so one formatter serves both.
 *
 * WHAT IT CAN SHOW. The payload carries dates and "is set" flags only — the
 * texts (reason, location, note) are never in the audit trail (Regel 8). So a
 * changed text reads as "geändert" / "erfasst" / "entfernt", never as its
 * content. Instants are formatted in Swiss wall-clock time (Regel 51).
 *
 * KND-3b adds the generated confirmation. Its namespaced
 * `termination_confirmation` payload carries the version id, number and
 * trigger only — never what the letter says — so the line reads
 * "Kündigungsbestätigung erstellt · Version N".
 *
 * KND-4a adds the accepted e-mail. Its namespaced
 * `termination_confirmation_delivery` payload carries ids, the version number
 * and a resend flag — never the address — so the line reads
 * "Kündigungsbestätigung versendet · Version N".
 */
import { formatDateOnly, formatDateTime } from "./datetime";

export const TERMINATION_AUDIT_ACTIONS = {
  tenancy_termination_recorded: "Kündigung erfasst",
  tenancy_termination_updated: "Kündigung geändert",
  tenancy_termination_withdrawn: "Kündigung zurückgenommen",
  termination_confirmation_generated: "Kündigungsbestätigung erstellt",
  termination_confirmation_sent: "Kündigungsbestätigung versendet",
};

/** The payload namespace each confirmation action carries its version in. */
const CONFIRMATION_PAYLOADS = {
  termination_confirmation_generated: "termination_confirmation",
  termination_confirmation_sent: "termination_confirmation_delivery",
};

/** The German label of a termination action, or null for any other action. */
export function terminationAuditActionLabel(action) {
  const key = String(action || "").toLowerCase();
  return TERMINATION_AUDIT_ACTIONS[key] || null;
}

const DATE_FIELDS = [
  ["notice_given_at", "Kündigung eingegangen am", formatDateOnly],
  ["termination_effective_date", "Ende des Mietverhältnisses", formatDateOnly],
  ["latest_checkout_at", "Spätestes Check-out", formatDateTime],
  ["handover_at", "Abnahme/Übergabetermin", formatDateTime],
];

const FLAG_FIELDS = [
  ["termination_reason_set", "Kündigungsgrund"],
  ["handover_location_set", "Übergabeort"],
  ["note_set", "Notiz"],
];

const payloadOf = (values) =>
  values && typeof values === "object" && values.termination &&
  typeof values.termination === "object"
    ? values.termination
    : null;

function flagChange(label, before, after) {
  if (Boolean(before) === Boolean(after)) return null;
  return {
    label,
    old: before ? "erfasst" : "—",
    new: after ? "erfasst" : "—",
  };
}

/**
 * `{ summary, changes }` for a termination row, or null when the row is not
 * one — the caller then continues with its own branches, unchanged.
 */
export function formatTerminationAudit(log) {
  const action = String(log?.action || "").toLowerCase();
  const label = terminationAuditActionLabel(action);
  if (!label) return null;
  if (CONFIRMATION_PAYLOADS[action]) {
    const confirmation = log.new_values?.[CONFIRMATION_PAYLOADS[action]];
    const version = Number.isInteger(confirmation?.version) ? confirmation.version : null;
    return {
      summary: version ? `${label} · Version ${version}` : label,
      changes: [],
    };
  }
  const before = payloadOf(log.old_values);
  const after = payloadOf(log.new_values);
  if (!before && !after) return { summary: label, changes: [] };

  if (action === "tenancy_termination_recorded") {
    const end = after?.termination_effective_date;
    return {
      summary: end ? `${label} · Ende ${formatDateOnly(end)}` : label,
      changes: [],
    };
  }
  if (action === "tenancy_termination_withdrawn") {
    return { summary: label, changes: [] };
  }

  const changes = [];
  for (const [key, fieldLabel, format] of DATE_FIELDS) {
    const o = before?.[key] || null;
    const n = after?.[key] || null;
    if (String(o || "") === String(n || "")) continue;
    changes.push({ label: fieldLabel, old: format(o), new: format(n) });
  }
  for (const [key, fieldLabel] of FLAG_FIELDS) {
    const change = flagChange(fieldLabel, before?.[key], after?.[key]);
    if (change) changes.push(change);
  }
  return { summary: label, changes };
}
